<template>
  <div>
    <h2 class="subtitle">{{ modelName }}</h2>
    <button
      type="button"
      v-if="!disableCreate"
      class="button is-small"
      @click="newItem"
    >
      New {{ modelName }}
    </button>

    <table
      class="table is-striped"
      :aria-label="modelName"
    >
      <thead>
        <tr>
          <th
            scope="col"
            v-for="field in Object.values(tableFieldsConfig).map(f => f.label)"
            :key="field"
          >
            {{ field }}</th>
          <!-- @slot Add headers to the table -->
          <slot name="extra-table-header" />
          <th scope="col"></th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="item in apiStoreClient.data"
          :key="item.id"
        >
          <template
            v-for="(config, name, index) in tableFieldsConfig"
            :key="name"
          >
            <th
              v-if="index === 0"
              scope="row"
              :style="
                // @ts-ignore
                getTdStyle(item, name, config)"
            >
              {{
                // @ts-ignore
                getTdContent(item, name, config)
              }}
            </th>
            <td
              v-else
              :style="
                // @ts-ignore
                getTdStyle(item, name, config)"
            >
              {{
                // @ts-ignore
                getTdContent(item, name, config)
              }}
            </td>
          </template>

          <!-- @slot add extra data to the table row, gives the slot the item `v-slot:extra-table-column="{ item }"` -->
          <slot
            name="extra-table-column"
            :item="item"
          ></slot>
          <td>
            <div class="buttons">
              <button
                v-if="!disableUpdate"
                type="button"
                class="button is-small"
                @click.stop="updateItem(item as TDetail)"
              >
                Update
              </button>
              <button
                v-if="instanceLink"
                type="button"
                class="button is-small"
                @click.stop="goToItem(item as TDetail, instanceLink)"
              >
                View
              </button>
              <button
                v-if="enableQuickDelete"
                type="button"
                class="button is-danger is-small"
                @click.stop="deleteItem(item as TDetail)"
              >
                Delete
              </button>
            </div>
          </td>
        </tr>
      </tbody>

    </table>

    <div
      class="buttons"
      v-if="apiStoreClient.previous || apiStoreClient.next"
    >
      <button
        v-if="apiStoreClient.previous"
        class="button"
        @click="apiStoreClient.previousPage()"
      >Previous</button>
      <button
        v-if="apiStoreClient.next"
        class="button"
        @click="apiStoreClient.nextPage()"
      >Next</button>
    </div>

    <Modal
      v-if="localValue && formFieldsConfig"
      :active="formActive"
      @close="formActive = false"
    >
      <!-- @vue-generic {TCreate, TDetail, TUpdate} -->
      <ModelForm
        :model-name="modelName"
        v-model="localValue"
        :client="apiStoreClient"
        :fields="formFieldsConfig.fields"
        @form-submitted="formActive = false; emit('form-submitted', $event)"
      >
        <template v-slot:extra-form-inputs>
          <!-- @slot forwarded to ModelForm -->
          <slot name="extra-form-inputs"></slot>
        </template>
      </ModelForm>
    </Modal>
  </div>
</template>

<script lang="ts">
export interface TableFieldConfig<TDetail, K extends keyof TDetail = keyof TDetail> {
  label: string
  formatter?: (value: TDetail[K]) => string | number
}

export type TableFieldsConfig<TDetail> = {
  [K in keyof TDetail]?: TableFieldConfig<TDetail, K>
}

export interface TableFormFieldsConfig<TCreate, TDetail extends DatabaseItem = TCreate & DatabaseItem, TUpdate extends DatabaseItem = TCreate & DatabaseItem> {
  fields: FormFieldsConfig<TCreate, TUpdate>
  dataItemToForm: (dataItem: TDetail) => TCreate | TUpdate
}

</script>

<script
  setup
  lang="ts"
  generic="TCreate extends object, TDetail extends DatabaseItem = TCreate & DatabaseItem, TUpdate extends DatabaseItem = TCreate & DatabaseItem"
>
import { computed, ref, type StyleValue } from 'vue';
import { useRouter } from 'vue-router';
import type { ApiStoreClient, DatabaseItem } from '../crud';
import Modal from './Modal.vue';
import ModelForm, { type FormFieldsConfig, type FormFormFieldConfig, type FormSubmittedEvent } from './ModelForm.vue';

type InstanceLink = InstanceLinkEvent | InstanceLinkRouter

interface InstanceLinkEvent {
  event: true
}

interface InstanceLinkRouter {
  link: string
  param?: keyof TDetail
}

const props = defineProps<{
  /** Name above the table, also passed to ModelForm */
  modelName: string,
  /** Value for the data in the form */
  modelValue?: Partial<TCreate | TUpdate>
  /** Config for ModelForm */
  formFieldsConfig?: {
    fields: FormFieldsConfig<TCreate, TUpdate>
    /** Function to convert a TDetail to TCreate | TUpdate, should return a new object */
    dataItemToForm: (dataItem: TDetail) => TCreate | TUpdate
  }
  /** If given will make the new item button go to the given Vue router name */
  newItemLink?: string
  /** ApiStoreClient to use for CRUD operations */
  apiStoreClient: ApiStoreClient<TCreate, TDetail, TDetail, TUpdate>
  /** Configuration of what to show in the table */
  tableFieldsConfig: TableFieldsConfig<TDetail>
  /** Hide the button to create a new item */
  disableCreate?: boolean
  /** Hide the button to update an item */
  disableUpdate?: boolean
  /** Show a button in the table to delete items */
  enableQuickDelete?: boolean
  /** Show a button for each item to go to a Vue router URL */
  instanceLink?: InstanceLink
}>()

const emit = defineEmits<{
  (e: "update:modelValue", value: Partial<TCreate | TUpdate>): void
  (e: 'form-submitted', value: FormSubmittedEvent<TDetail>): void
  (e: 'view-item', value: TDetail): void
}>()

// Render table

function getTdContent(item: TDetail, fieldName: keyof TDetail, field: TableFieldConfig<TDetail>) {
  if (field.formatter) {
    return field.formatter(item[fieldName])
  }
  return item[fieldName]
}

function getTdStyle(item: TDetail, fieldName: keyof TDetail, field: TableFieldConfig<TDetail>): StyleValue {
  const content = getTdContent(item, fieldName, field)
  if (typeof content === 'number') {
    return { textAlign: 'right' as const }
  }
  return {}
}

function goToItem(item: TDetail, instanceLink: InstanceLink) {
  if ('event' in instanceLink) {
    emit('view-item', item)
  } else {
    let link = instanceLink.link
    if (instanceLink.param) {
      const value = item[instanceLink.param]
      link = link.replace("{}", `${value}`)
    }
    router.push(link)
  }
}

// Form things

const localValue = computed({
  get() {
    return props.modelValue
  },
  set(newValue) {
    if (!newValue) return
    emit("update:modelValue", newValue)
  }
})

const formActive = ref(false)
const router = useRouter()

function newItem() {
  if (props.newItemLink) {
    router.push({ name: props.newItemLink })
  } else {
    resetForm()
    formActive.value = true
  }
}

function updateItem(item: TDetail) {
  if (!props.formFieldsConfig) return
  localValue.value = props.formFieldsConfig.dataItemToForm(item)
  formActive.value = true
}

function deleteItem(item: TDetail) {
  props.apiStoreClient.delete(item.id)
  emit("form-submitted", { action: "delete", id: item.id })
}

/** Copy of the function in ModelForm */
function resetForm() {
  if (!props.formFieldsConfig) return
  for (const [fieldName, configUntyped] of Object.entries(props.formFieldsConfig)) {
    const config = configUntyped as FormFormFieldConfig
    if (config.resetValue === undefined) continue
    // @ts-expect-error - localValue could be TCreate, while props.fields could be TUpdate
    // But implementers shouldn't do that.
    localValue.value[fieldName] = config.resetValue
  }
}
</script>